import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { fetchTemplateStructure } from './hrStructureService';
import { DEFAULT_HR_SECTIONS } from '../utils/hrMaterials';
import { listAllJobTreadImports } from './templatesImportsService';
import { listClaudeSkills } from './claudeSkillsService';
import { getAllCourses, getCourseById } from './firebase/courses';

/**
 * Individual content the portal sidebar search can find — not just pages.
 * Covers every library a client browses: Sales / Finance / HR materials, SOPs,
 * KPIs, job descriptions, automations, JobTread imports, Claude skills,
 * courses and their lessons, and the software/tools directory.
 *
 * Each entry is the same shape as the static PORTAL_SEARCH_ENTRIES plus an
 * optional `gate` path used for access checks when the item's own path isn't
 * under a mapped portal route (e.g. /portal/automation/<id>).
 *
 * Every path deep-links to the item itself:
 *   materials  → ?material=<title>  (scrolls to + highlights the card)
 *   SOPs / JDs → ?open=<docId>      (opens the document modal)
 *   KPIs       → ?open=<role title> (opens the role modal)
 *   imports    → ?type=<group>&highlight=<id>
 *   skills     → ?highlight=<id>
 *   courses    → /portal/courses/<id>  (lessons deep-link to the lesson route)
 *   software   → ?search=<name>        (pre-fills the All Software search box)
 *
 * Loading is lazy (the sidebar only calls this once the user engages the search
 * box) and memoized per company for the life of the page.
 */

const DEPARTMENT_LABELS = {
  design: 'Design',
  construction: 'Construction',
  office: 'Office',
};

const norm = (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();

const departmentLabel = (department) =>
  DEPARTMENT_LABELS[String(department || '').toLowerCase()] || 'General';

/** One failing source must never blank out the rest of the index. */
const safe = (label, promise) =>
  promise
    .then((rows) => {
      console.warn(`[Portal search] source loaded: ${label} (${rows.length})`);
      return rows;
    })
    .catch((err) => {
      console.error(`Portal search: failed to load ${label}`, err);
      return [];
    });

const materialEntries = async ({ structureId, defaults, basePath, pillar, extraKeywords }) => {
  const sections = await fetchTemplateStructure(structureId, defaults);
  return sections.flatMap((section) =>
    (section.items || []).map((item) => ({
      path: `${basePath}?material=${encodeURIComponent(item.title)}`,
      title: item.name || item.title,
      section: `${pillar} · ${section.label}`,
      keywords: norm(
        `${item.title} ${item.name || ''} ${item.description || ''} ${section.label} ${pillar} ${extraKeywords}`
      ),
    }))
  );
};

const sopEntries = async (companyId) => {
  const [templateSnap, accountSnap] = await Promise.all([
    getDocs(collection(db, 'sopTemplates')),
    // Account-specific SOPs live under the client's own account doc.
    companyId
      ? getDocs(collection(db, 'accounts', companyId, 'sops')).catch(() => null)
      : Promise.resolve(null),
  ]);
  const docs = [
    ...templateSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    ...(accountSnap ? accountSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : []),
  ];
  return docs.map((sop) => ({
    // Clients browse SOPs inside the Operations hub tab, not the standalone
    // /portal/sops route — but the sops access toggle still gates them.
    path: `/portal/operations/sops?open=${encodeURIComponent(sop.id)}`,
    gate: '/portal/sops',
    title: sop.title || 'Untitled SOP',
    section: `SOPs · ${departmentLabel(sop.department)}`,
    keywords: norm(
      `${sop.title || ''} ${sop.description || ''} ${departmentLabel(sop.department)} sop sops standard operating procedure process`
    ),
  }));
};

const kpiEntries = async () => {
  const snap = await getDocs(collection(db, 'kpiTemplates'));
  // The portal renders the first template it finds — mirror that exactly so the
  // deep link always matches a role that's actually on the page.
  const template = snap.docs[0]?.data();
  const byDepartment = template?.departmentKPIs || {};
  return Object.entries(byDepartment).flatMap(([department, roles]) =>
    (roles || [])
      .filter((role) => role?.title)
      .map((role) => ({
        // KPIs live in the Operations hub tab; the kpis toggle still gates them.
        path: `/portal/operations/kpis?open=${encodeURIComponent(role.title)}`,
        gate: '/portal/kpis',
        title: role.title,
        section: `KPIs · ${departmentLabel(department)}`,
        keywords: norm(
          `${role.title} ${departmentLabel(department)} kpi kpis metrics scorecard targets ${(role.kpis || [])
            .map((kpi) => `${kpi.title || ''} ${kpi.label || ''}`)
            .join(' ')}`
        ),
      }))
  );
};

const jobDescriptionEntries = async () => {
  const snap = await getDocs(collection(db, 'jobDescriptions'));
  return snap.docs.map((d) => {
    const job = d.data();
    return {
      // Job descriptions live in the Operations hub tab; their own toggle gates them.
      path: `/portal/operations/job-descriptions?open=${encodeURIComponent(d.id)}`,
      gate: '/portal/job-descriptions',
      title: job.title || 'Untitled role',
      section: `Job descriptions · ${departmentLabel(job.department)}`,
      keywords: norm(
        `${job.title || ''} ${job.description || ''} ${departmentLabel(job.department)} job description role position hiring responsibilities`
      ),
    };
  });
};

const automationEntries = async () => {
  const snap = await getDocs(collection(db, 'automations'));
  return snap.docs.map((d) => {
    const automation = d.data();
    return {
      path: `/portal/automation/${d.id}`,
      title: automation.name || automation.title || 'Untitled automation',
      section: 'Technology · Automations',
      gate: '/portal/technology',
      keywords: norm(
        `${automation.name || ''} ${automation.title || ''} ${automation.description || ''} ${automation.category || ''} ${(
          automation.softwares || []
        )
          .map((s) => s?.name || s)
          .join(' ')} automation workflow zapier integration`
      ),
    };
  });
};

const jobTreadImportEntries = async () => {
  const rows = await listAllJobTreadImports();
  return rows.map((row) => ({
    path: `/portal/technology/jobtread-imports?type=${encodeURIComponent(row.tabId || 'all')}&highlight=${encodeURIComponent(row.id)}`,
    title: row.title || row.fileName || 'Untitled import',
    section: 'Technology · JobTread imports',
    keywords: norm(
      `${row.title || ''} ${row.description || ''} ${row.fileName || ''} ${row.tabId || ''} jobtread import template cost groups schedules dashboards todos csv json`
    ),
  }));
};

const claudeSkillEntries = async () => {
  const rows = await listClaudeSkills();
  return rows.map((row) => ({
    path: `/portal/technology/claude-skills?highlight=${encodeURIComponent(row.id)}`,
    title: row.title || row.skillFile?.fileName || 'Untitled skill',
    section: 'Technology · Skills',
    keywords: norm(
      `${row.title || ''} ${row.description || ''} ${row.skillFile?.fileName || ''} claude skill ai agent download`
    ),
  }));
};

const courseAndLessonEntries = async () => {
  // Mirror client visibility exactly (see collectCourseContentItems): published
  // courses only, and every lesson except those explicitly hidden.
  const published = (await getAllCourses()).filter((c) => c.isPublished);
  const detailed = await Promise.all(
    published.map((c) => getCourseById(c.id).catch(() => null))
  );

  const entries = published.map((course) => ({
    path: `/portal/courses/${course.id}`,
    title: course.title || 'Untitled course',
    section: `Courses · ${course.category || 'General'}`,
    keywords: norm(
      `${course.title || ''} ${course.description || ''} ${course.category || ''} course training learning video lessons`
    ),
  }));

  for (const course of detailed.filter(Boolean)) {
    for (const mod of course.modules || []) {
      for (const lesson of mod.lessons || []) {
        if (lesson.status === 'hidden' || !lesson.title) continue;
        const documentNames = (lesson.documents || [])
          .map((d) => d?.displayName || d?.name || '')
          .join(' ');
        entries.push({
          path: `/portal/courses/${course.id}/modules/${mod.id}/lessons/${lesson.id}`,
          title: lesson.title,
          section: `Courses · ${course.title || 'Course'}`,
          keywords: norm(
            `${lesson.title} ${lesson.description || ''} ${mod.title || ''} ${course.title || ''} ${course.category || ''} ${documentNames} lesson video training course module`
          ),
        });
      }
    }
  }

  return entries;
};

const softwareEntries = async () => {
  // Same walk AllSoftware.js does: technologyStack/{tier}/types/*/softwares.
  const tiers = ['Core', 'Primary', 'Secondary'];
  const entries = [];
  await Promise.all(
    tiers.map(async (tier) => {
      const typesSnap = await getDocs(collection(db, 'technologyStack', tier, 'types'));
      await Promise.all(
        typesSnap.docs.map(async (typeDoc) => {
          const typeName = typeDoc.data()?.name || '';
          const softwareSnap = await getDocs(
            collection(db, 'technologyStack', tier, 'types', typeDoc.id, 'softwares')
          );
          softwareSnap.forEach((softwareDoc) => {
            const data = softwareDoc.data();
            if (!data?.name) return;
            const useCases = Array.isArray(data.useCases)
              ? data.useCases.join(' ')
              : data.useCases || '';
            entries.push({
              path: `/portal/technology/all-software?search=${encodeURIComponent(data.name)}`,
              title: data.name,
              section: `Tools · ${typeName || tier}`,
              keywords: norm(
                `${data.name} ${data.description || ''} ${typeName} ${tier} ${useCases} software tool app tech stack`
              ),
            });
          });
        })
      );
    })
  );
  return entries;
};

/** Later duplicates lose — paths double as React keys in the results list. */
const dedupeByPath = (entries) => {
  const seen = new Set();
  return entries.filter((entry) => {
    if (!entry?.path || seen.has(entry.path)) return false;
    seen.add(entry.path);
    return true;
  });
};

const cache = new Map();

/**
 * Every individually searchable portal item. Memoized per company id, so the
 * collections are read once per page load however often search is opened.
 */
export const loadPortalContentSearchEntries = (companyId = null) => {
  const key = companyId || 'no-company';
  if (!cache.has(key)) {
    console.warn(`[Portal search] fetching content index (company=${key})`);
    const promise = Promise.all([
      safe(
        'sales materials',
        materialEntries({
          structureId: 'sales',
          defaults: [],
          basePath: '/portal/sales/resources',
          pillar: 'Sales materials',
          extraKeywords: 'sales material template document proposal contract resource download tailor',
        })
      ),
      safe(
        'operations materials',
        materialEntries({
          structureId: 'operations',
          defaults: [],
          basePath: '/portal/operations/materials',
          pillar: 'Operations materials',
          extraKeywords: 'operations material template document checklist resource download tailor',
        })
      ),
      safe(
        'finance materials',
        materialEntries({
          structureId: 'finance',
          defaults: [],
          basePath: '/portal/finance/materials',
          pillar: 'Finance materials',
          extraKeywords: 'finance material template document resource download tailor',
        })
      ),
      safe(
        'HR materials',
        materialEntries({
          structureId: 'hr',
          defaults: DEFAULT_HR_SECTIONS,
          basePath: '/portal/hr/materials',
          pillar: 'HR materials',
          extraKeywords: 'hr material template document form resource download tailor',
        })
      ),
      safe('SOPs', sopEntries(companyId)),
      safe('KPIs', kpiEntries()),
      safe('job descriptions', jobDescriptionEntries()),
      safe('automations', automationEntries()),
      safe('JobTread imports', jobTreadImportEntries()),
      safe('Skills', claudeSkillEntries()),
      safe('courses and lessons', courseAndLessonEntries()),
      safe('software tools', softwareEntries()),
    ])
      .then((groups) => {
        const entries = dedupeByPath(groups.flat());
        console.warn(
          `[Portal search] content index loaded: ${entries.length} entries`,
          groups.map((g, i) => `${['sales', 'operations', 'finance', 'hr', 'sops', 'kpis', 'jds', 'automations', 'imports', 'skills', 'courses', 'tools'][i]}=${g.length}`).join(' ')
        );
        // An all-empty index means every source failed — don't cache that for
        // the life of the page; let the next search open retry.
        if (!entries.length) cache.delete(key);
        return entries;
      })
      .catch((err) => {
        // A total failure shouldn't poison the cache — let the next open retry.
        console.error('Portal search: content index failed', err);
        cache.delete(key);
        return [];
      });
    cache.set(key, promise);
  }
  return cache.get(key);
};
